import React from 'react';
import { isEmpty } from 'lodash';
import { TrackingSectionIcon } from 'taboola-ultimate-ui';
import { CollapsibleFormSection } from 'components';
import CommonCollapsibleCard from 'components/CommonCollapsibleCard/CommonCollapsibleCard';
import { useDropdownOptions } from 'hooks';
import SectionHeader from 'modules/campaigns/components/Form/SectionHeader/SectionHeader';
import { ExternalMetadataWrapper } from 'modules/campaigns/modules/common-campaign-form/components/ExternalMetadata/ExternalMetadataWrapper';
import { useCampaignExternalMetadataFormFieldValue } from 'modules/campaigns/modules/common-campaign-form/components/ExternalMetadata/useExternalMetadataFormFieldValue';
import ThirdPartyTags from 'modules/campaigns/modules/common-campaign-form/components/ThirdPartyTags';
import { useThirdPartyTagsFormFieldValue } from 'modules/campaigns/modules/common-campaign-form/components/ThirdPartyTags/useThirdPartyTagsFormFieldValue';
import { enumTransformer } from 'modules/campaigns/services/utils/dropdownUtils';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { CAMPAIGN_EXTERNAL_METADATA_KEYS } from 'modules/taboola-common-frontend-modules/externalMetadata/config/externalMetadataKeys';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { TrackingSectionSubtitle } from './TrackingSectionSubtitle';

const externalDataDeletionDisabled = FEATURE_FLAGS.CAMPAIGN_EXTERNAL_DATA_DELETION_DISABLED;
const externalDataReadOnlyFields = FEATURE_FLAGS.CAMPAIGN_EXTERNAL_DATA_READONLY_FIELDS;

export const TrackingSection = ({ children, isInitiallyCollapsed }) => {
    const { value: thirdPartyTags, isPermitted: isThirdPartyTagsPermitted } = useThirdPartyTagsFormFieldValue();
    const campaignExternalMetadataEnabled = useConfigMatch({
        [FEATURE_FLAGS.CAMPAIGN_EXTERNAL_METADATA_ENABLED]: 'false',
    });
    const isExternalMetadataPermitted =
        usePermissions('CAMPAIGN_EXTERNAL_METADATA_ENABLED') || campaignExternalMetadataEnabled === 'true';
    const { value: externalMetadata } = useCampaignExternalMetadataFormFieldValue(isExternalMetadataPermitted);
    const isAdvancedOptionsEmpty = isEmpty(thirdPartyTags) && isEmpty(externalMetadata);
    const isAdvancedOptionsPermitted = isThirdPartyTagsPermitted || isExternalMetadataPermitted;

    const campaignExternalMetadataKeyOptions = useDropdownOptions(
        CAMPAIGN_EXTERNAL_METADATA_KEYS,
        'externalMetadata.editor.campaign',
        enumTransformer
    );

    return (
        <CommonCollapsibleCard
            id="CAMPAIGN_TRACKING"
            header={
                <SectionHeader
                    headerIcon={<TrackingSectionIcon />}
                    headerText={<FormattedMessage id="campaign.editor.tracking" />}
                />
            }
            isInitiallyCollapsed={isInitiallyCollapsed}
        >
            {children}
            {isAdvancedOptionsPermitted && (
                <CollapsibleFormSection
                    isInitiallyCollapsed={isAdvancedOptionsEmpty}
                    title={<FormattedMessage id="campaign.advancedOptions" defaultMessage="Advanced Options" />}
                    subtitle={
                        <TrackingSectionSubtitle
                            isThirdPartyTagsPermitted={isThirdPartyTagsPermitted}
                            isExternalMetadataPermitted={isExternalMetadataPermitted}
                        />
                    }
                    sectionId="CAMPAIGN_SUBSECTION_ADVANCED_TRACKING"
                    testId="tracking-advanced-options"
                >
                    {isThirdPartyTagsPermitted && <ThirdPartyTags />}
                    {isExternalMetadataPermitted && (
                        <ExternalMetadataWrapper
                            externalDataDeletionDisabled={externalDataDeletionDisabled}
                            externalDataReadOnlyFields={externalDataReadOnlyFields}
                            externalMetadataKeyOptions={campaignExternalMetadataKeyOptions}
                            msgIdPrefix="campaign"
                        />
                    )}
                </CollapsibleFormSection>
            )}
        </CommonCollapsibleCard>
    );
};

export default TrackingSection;
